import { AlertTriangle, CheckCircle2, CircleHelp, Clock, ShieldCheck } from "lucide-react";
import { Link } from "react-router";

import { ENVIRONMENT_LABEL, EnvironmentBadge } from "@/components/app-registration-badges";
import { IT_APPROVALS_PATH } from "@/components/app-registration-permission-change";
import { type ProjectSummary, type SignInEnvironmentStatus, useProjectSignIn } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface SignInConsentNoticeProps {
  consentState: SignInEnvironmentStatus["consent_state"];
  consentedAt: string | null | undefined;
  /** What "never approved yet" means where this line is shown. */
  pendingText: string;
}

/**
 * Where IT's tenant-wide consent of a sign-in registration stands, in one line.
 *
 * `pending` with a `consented_at` is not "never approved": IT approved it once
 * and an amendment has since added permissions, which Entra does not fold into
 * the existing grant. Sign-in still works on that grant, so it reads as new
 * permissions waiting rather than as a locked-out app.
 *
 * `reported` is Entra's redirect saying an admin consented, without Scaffold UI
 * confirming it from the grant (unreadable, or not shown yet) — shown as such,
 * never as approved.
 */
export function SignInConsentNotice({
  consentState,
  consentedAt,
  pendingText,
}: SignInConsentNoticeProps) {
  if (consentState === "consented") {
    return (
      <p className="flex items-start gap-1.5 text-xs text-green-800 dark:text-green-300">
        <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" />
        <span>Approved by IT on {formatDate(consentedAt ?? null)}</span>
      </p>
    );
  }
  if (consentState === "reported") {
    return (
      <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
        <CircleHelp className="mt-0.5 size-3.5 shrink-0" />
        <span>
          IT reported approving it on {formatDate(consentedAt ?? null)} — not verified yet.
        </span>
      </p>
    );
  }
  if (consentState !== "pending") {
    return null;
  }
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
      <Clock className="mt-0.5 size-3.5 shrink-0" />
      <span>
        {consentedAt
          ? "New permissions are waiting for IT approval — sign-in keeps working meanwhile."
          : pendingText}
      </span>
    </p>
  );
}

interface ProjectSignInSectionProps {
  project: ProjectSummary;
  isAdmin: boolean;
}

/**
 * How the project's app signs people in, on the Details tab.
 *
 * Visible to everyone who can see the project: "waiting for IT approval" is the
 * answer to "why can nobody sign in yet?", which members ask as much as owners.
 * The fallback reason and reconcile problems are owner/admin detail — the
 * server leaves them out for anyone else, so they are simply absent here.
 *
 * A plain shared project (no fallback reason) renders nothing, so the tab looks
 * exactly as it did before projects could have their own sign-in.
 */
export function ProjectSignInSection({ project, isAdmin }: ProjectSignInSectionProps) {
  const dedicated = project.sso_mode === "dedicated";
  const { data, isLoading, error } = useProjectSignIn(project.id, dedicated);

  if (!dedicated) {
    if (!project.sso_fallback_reason) {
      return null;
    }
    return (
      <div className="space-y-1 rounded-lg border p-3">
        <p className="text-sm font-medium">Sign-in</p>
        <p className="text-muted-foreground text-xs break-words">
          Signs in with the shared Norican sign-in app: {project.sso_fallback_reason}
        </p>
      </div>
    );
  }

  const environments = data?.environments ?? [];

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">Sign-in</p>
        {isAdmin && (
          <Link
            to={IT_APPROVALS_PATH}
            className="text-primary inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
          >
            <ShieldCheck className="size-3.5" />
            IT approvals
          </Link>
        )}
      </div>
      <p className="text-muted-foreground text-xs">
        This project signs people in with its own app registration, one per environment.
      </p>

      {isLoading && <p className="text-muted-foreground text-xs">Checking sign-in…</p>}

      {!isLoading && error && (
        <p className="text-destructive flex items-start gap-1.5 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>Couldn&apos;t load the sign-in status. Reopen the project to retry.</span>
        </p>
      )}

      {environments.map((env) => (
        <SignInEnvironmentRow key={env.environment} env={env} />
      ))}
    </div>
  );
}

function SignInEnvironmentRow({ env }: { env: SignInEnvironmentStatus }) {
  return (
    <div className="space-y-1.5 border-t pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <EnvironmentBadge environment={env.environment} />
        <span className="font-mono text-xs font-medium">{env.display_name}</span>
      </div>
      {env.entra_app_id && (
        <p className="text-muted-foreground text-xs">
          Client ID <span className="text-foreground font-mono break-all">{env.entra_app_id}</span>
        </p>
      )}
      <SignInConsentNotice
        consentState={env.consent_state}
        consentedAt={env.consented_at}
        pendingText={`Waiting for IT approval — nobody can sign in to ${ENVIRONMENT_LABEL[env.environment]} until an IT admin approves this app registration.`}
      />
      {env.reconcile_error && (
        <p className="text-destructive flex items-start gap-1.5 text-xs break-words">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{env.reconcile_error}</span>
        </p>
      )}
    </div>
  );
}
